import { t } from 'i18next'
import * as yup from 'yup'

import { truncateWithEllipses } from '../../../../../helpers'

const basicSchema = {
  fileLanguage: yup
    .object()
    .required('documentForm.errorRequired')
    .nullable()
    .shape({
      id: yup.string().required('documentForm.errorRequired'),
    }),
  type: yup.object().required('documentForm.errorRequired').nullable(),
}

function convertLaravelRuleArrayToYup(
  rules: string[],
  key: string
): yup.AnySchema {
  // Default to a string schema; will adjust based on rules.
  let schema: yup.AnySchema = yup
    .string()
    .typeError(t('documentForm.errorRequired') as string)

  // If a rule indicates a numeric type, change the schema to number.
  if (rules.includes('integer') || rules.includes('numeric')) {
    schema = yup.number()
  }

  // If a rule indicates an array, change to string array.
  if (rules.includes('array')) {
    if (rules.includes('required')) {
      schema = yup
        .array()
        .of(yup.string())
        .required(t('documentForm.errorRequired') as string)
        .min(1, t('documentForm.errorRequired') as string)
    } else {
      schema = yup.array().of(yup.string())
    }
  }

  // exception for keywords
  if (key === 'keywords') {
    schema = yup.array().of(yup.string())
  }

  // Process each rule and chain the corresponding Yup validators.
  rules.forEach((ruleItem) => {
    if (typeof ruleItem !== 'string') return
    const [ruleName, ruleParam] = ruleItem.split(':').map((s) => s.trim())

    switch (ruleName) {
      case 'required':
        schema = schema.required(t('documentForm.errorRequired') as string)
        break
      case 'min':
        // For strings, use min length; for numbers, use min value.
        // If field is not required, use test rule instead to prevent field becoming "required".
        if (schema instanceof yup.StringSchema) {
          if (rules.includes('required')) {
            schema = (schema as yup.StringSchema).min(
              Number(ruleParam),
              t('documentForm.errorMinimum', { min: ruleParam }) as string
            )
          } else {
            schema = (schema as yup.StringSchema).test(
              'min-length-if-not-empty',
              t('documentForm.errorMinimum', { min: ruleParam }) as string,
              (value) => !value || value.length >= Number(ruleParam)
            )
          }
        } else if (schema instanceof yup.NumberSchema) {
          if (rules.includes('required')) {
            schema = (schema as yup.NumberSchema).min(
              Number(ruleParam),
              t('documentForm.errorMinimumNumber', { min: ruleParam }) as string
            )
          } else {
            schema = (schema as yup.NumberSchema).test(
              'min-value-if-not-empty',
              t('documentForm.errorMinimumNumber', {
                min: ruleParam,
              }) as string,
              (value) => !value || value >= Number(ruleParam)
            )
          }
        }
        break
      case 'max':
        // For strings, use max length; for numbers, use max value.
        if (schema instanceof yup.StringSchema) {
          schema = (schema as yup.StringSchema).max(
            Number(ruleParam),
            `Maximum length is ${ruleParam}`
          )
        } else if (schema instanceof yup.NumberSchema) {
          schema = (schema as yup.NumberSchema).max(
            Number(ruleParam),
            `Maximum value is ${ruleParam}`
          )
        }
        break
      case 'not_in':
        const trimmedParameter = ruleParam.replace(/^"(.*)"$/, '$1')
        schema = (schema as yup.StringSchema).test(
          'value-not-in',
          t('documentForm.errorNotIn', {
            word: truncateWithEllipses(trimmedParameter, 50),
          }) as string,
          (value) => {
            return value !== trimmedParameter
          }
        )
        break
      case 'email':
        // Force schema to be a string if not already.
        if (!(schema instanceof yup.StringSchema)) {
          schema = yup.string()
        }
        schema = (schema as yup.StringSchema).email('Invalid email format')
        break
      // Add additional rules as needed.
      default:
        break
    }
  })

  return schema
}

export const documentFormSchema = (
  isMustChangeEnabled: boolean,
  validationRules: any
) => {
  const shape: Record<string, yup.AnySchema> = {}

  // ignore the following list as they're not part of the form, but filled when submitting
  const ignoreList = [
    'contact_user_id',
    'language_code',
    'access_rights',
    'main_topic_id',
  ]

  for (let key of Object.keys(validationRules)) {
    const k = key.replace('metadata.', '')
    if (ignoreList.includes(k)) continue
    shape[k] = convertLaravelRuleArrayToYup(validationRules[key], k)
  }

  return yup.object().shape({
    ...basicSchema,
    metadata: yup.array().of(yup.object({ ...shape })),
  })
}
